import React from 'react';
import {
  Label,
  TextField,
  Text,
  MaskedTextField,
  Icon,
  CompactPeoplePicker,
  IBasePickerSuggestionsProps,
  ISelectableOption,
  ComboBox,
  IComboBox,
  IComboBoxStyles,
  Stack,
  IDropdownOption,
} from '@fluentui/react';
import { IComboBoxOption, Dropdown, Image, ChoiceGroup } from '@fluentui/react';
import { Club } from '../../../system/Club/Club';
import { DocumentoTipo } from '../../../system/DocumentoTipo/DocumentoTipo';
import RegistroEntrenadorForm from '../../RegistroEntrenador/RegistroEntrenadorForm';
import CustomInputIfc from '../../../lib/FormValidator/Form/CustomInputIfc';
import InputField from '../../../lib/FormValidator/Form/InputField';
import { DatePickerInput } from '../../../lib/FormValidator/Form/DatePickerInput';
import ComboBoxInput from '../../../lib/FormValidator/Form/ComboBoxInput';
import FederationBoxNoBG from '../../Common/FederationBoxNoBG';
import SystemClient from '../../../system/SystemClient';
import MessageCourier from '../../../lib/MessageCourier';
import { useCustomInput } from '../../../common/custom-hooks/useCustomInput';
import SingleFileDropzone from '../../Common/FileDropzone/SingleFileDropzone';
import { checkMaxSize } from '../WizardRegistroEntrenador';
import { formatFileSize, obtenerExtension, isAdult } from '../WizardRegistroEntrenador';

interface IDatosIniciales {
  documentoTipos: DocumentoTipo[];
  clubes: Club[];
  form: RegistroEntrenadorForm;
  onBlurCustomInput: (customInput: CustomInputIfc) => void;
  system: SystemClient;
  courier: MessageCourier;
  onDropSelfie: (file: File) => void;
  onDropDocumento: (file: File) => void;
  fileSelfie: File | null;
  fileDocumento: File | null;
}

export const DatosIniciales = ({
  documentoTipos,
  clubes,
  form,
  onBlurCustomInput,
  system,
  courier,
  onDropSelfie,
  onDropDocumento,
  fileSelfie,
  fileDocumento,
}: IDatosIniciales) => {

  const [anioNacimiento, setAnioNacimiento] = React.useState<string>('');
  const [diaNacimiento, setDiaNacimiento] = React.useState<string>('');
  const [mesNacimiento, setMesNacimiento] = React.useState<string>('');

  const [fechaNacimientoIsValid, setFechaNacimientoIsValid] = React.useState<boolean>(true);


  const federacionInput = useCustomInput(() => form.getFedIdInput(), [form]);

  const clubesOptions: IComboBoxOption[] = React.useMemo(() => {
    if (clubes)
      return clubes.map((club) => ({
        key: club.getId()!,
        text: club.getSigla() ? club.getSigla()! : '',
        data: club,
      }));
    else return [];
  }, [clubes]);

  const documentoTiposOptions: IComboBoxOption[] = React.useMemo(() => {
    if (documentoTipos)
      return documentoTipos.map((documentoTipo) => ({
        key: documentoTipo.getId()!,
        text: documentoTipo.getDescripcion(),
      }));
    else return [];
  }, [documentoTipos]);

  const comboStyle: IComboBoxStyles = {
    container: undefined,
    label: undefined,
    labelDisabled: undefined,
    root: undefined,
    rootError: undefined,
    rootDisallowFreeForm: undefined,
    rootHovered: undefined,
    rootPressed: undefined,
    rootFocused: undefined,
    rootDisabled: undefined,
    input: undefined,
    inputDisabled: undefined,
    errorMessage: undefined,
    callout: { maxHeight: '200px', minWidth: '-webkit-fill-available;' },
    optionsContainerWrapper: { maxHeight: '200px', minWidth: '-webkit-fill-available;' },
    optionsContainer: { maxHeight: '200px', minWidth: '-webkit-fill-available;' },
    header: undefined,
    divider: undefined,
    screenReaderText: undefined,
  };

  const GeneroOptions = [
    { key: 'M', text: 'Hombre' },
    { key: 'F', text: 'Mujer' },
  ];

  const yearOptions: IDropdownOption[] = Array.from({ length: 100 }, (_, i) => ({
    key: 1923 + i,
    text: (1923 + i).toString(),
  }));

  const monthOptions: IDropdownOption[] = [
    { key: '01', text: 'January' },
    { key: '02', text: 'February' },
    { key: '03', text: 'March' },
    { key: '04', text: 'April' },
    { key: '05', text: 'May' },
    { key: '06', text: 'June' },
    { key: '07', text: 'July' },
    { key: '08', text: 'August' },
    { key: '09', text: 'September' },
    { key: '10', text: 'October' },
    { key: '11', text: 'November' },
    { key: '12', text: 'December' },
  ];

  const dayOptions: IDropdownOption[] = Array.from({ length: 31 }, (_, i) => ({
    key: i + 1,
    text: (i + 1).toString(),
  }));


  React.useEffect(() => {
    if (anioNacimiento && mesNacimiento && diaNacimiento) {
      const fechaNacimiento = new Date(+anioNacimiento, +mesNacimiento - 1, +diaNacimiento);
      if (fechaNacimiento.toString() !== 'Invalid Date') {
        console.log('set date')
        form.getFechaNacimientoInput().setValue(fechaNacimiento);
        onBlurCustomInput(form.getFechaNacimientoInput());

        if (isAdult(fechaNacimiento)) {
          setFechaNacimientoIsValid(true);
        } else {
          setFechaNacimientoIsValid(false);
        }
      }
    }
  }, [anioNacimiento, mesNacimiento, diaNacimiento]);



  return (
    <div>
      <div className="ms-Grid" dir="ltr">
        <Text
          className="mb-1"
          variant="small"
          styles={{ root: { textAlign: 'center', color: '#666', display: 'block' } }}
        >
          Solo para Instructores/as y entrenadores/as de Handball que no tengan nivelación previa o a quienes
          les sea requerido el registro.
        </Text>

        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 form-item">
            <InputField input={form.getNombreInput()} label="Nombre (*)" onBlur={onBlurCustomInput} />
          </div>
        </div>

        <div className="ms-Grid-row mt-2">
          <div className="ms-Grid-col ms-sm12 form-item">
            <InputField input={form.getApellidoInput()} label="Apellido (*)" onBlur={onBlurCustomInput} />
          </div>
        </div>

        <div className="ms-Grid-row mt-2">
          <div className="ms-Grid-col ms-sm3 form-item">
            {/* <Label>Tipo</Label>
            <Dropdown
              options={documentoTiposOptions}
              selectedKey={selectedTipoDocumentoId}
              onChange={(e, opt: any) => setSelectedTipoDocumentoId(opt.text.toString())}
            ></Dropdown> */}
            {documentoTiposOptions && (
              <ComboBoxInput
                input={form.getDtidInput()}
                label={'Tipo (*)'}
                onBlur={onBlurCustomInput}
                options={documentoTiposOptions}
                selectedKey={form.getDtidInput().getValue()}
              />
            )}
          </div>
          <div className="ms-Grid-col ms-sm9 form-item">
            <InputField
              input={form.getDocumentoNumeroInput()}
              label="Documento numero (*)"
              onBlur={onBlurCustomInput}
            />
          </div>
        </div>

        <div className="ms-Grid-row mt-2">
          {/* <div className="ms-Grid-col ms-sm6 form-item">
            <Stack horizontalAlign="center" className="text-center mt-1">
              {' '}
              <SingleFileDropzone
                label="Documento"
                icon="Camera"
                onDrop={(file) => onDropSelfie(file)}
              ></SingleFileDropzone>
            </Stack>{' '}
            <div className="text-center">
              <Text variant="xSmall" className="" title="Formatos admitidos">
                (JPG, JPEG, GIF, PNG)
              </Text>
            </div>
            {fileSelfie && (
              <div className="mt-2 text-center">
                <Text block variant="medium" className="">
                  <Icon iconName="SkypeCircleCheck" style={{ color: '#107C10', marginRight: 10 }}></Icon>
                  Archivo {obtenerExtension(fileSelfie.name)} cargado - {formatFileSize(fileSelfie.size)}
                </Text>
                {!checkMaxSize(fileSelfie.size) && (
                  <Text block variant="medium" className="error-color">
                    El tamaño de la foto es demasiado grande, el maximo es de 20MB
                  </Text>
                )}
              </div>
            )}
          </div> */}

          {/* <div className="ms-Grid-col ms-sm6">
            <DatePickerInput
              input={form.getFechaNacimientoInput()}
              label="Fecha de nacimiento"
              onBlur={(e) => {
                onBlurCustomInput(e);
              }}
            />
          </div> */}

          <div className="ms-Grid-col ms-sm4 form-item">
            <TextField
              value={diaNacimiento}
              // onChange={(ev, newValue) => setDiaNacimiento(newValue!)}
              label="Día"
              onChange={(ev, newValue) => {
                setDiaNacimiento(newValue!);
              }}
              errorMessage={
                diaNacimiento && (+diaNacimiento < 1 || +diaNacimiento > 31)
                  ? 'Día debe estar entre 1 y 31'
                  : undefined
              }
            />
          </div>
          <div className="ms-Grid-col ms-sm4 form-item">
            <Dropdown
              label="Mes"
              selectedKey={mesNacimiento}
              onChange={(e, option) => setMesNacimiento(option?.key.toString()!)}
              options={monthOptions}
            />
          </div>
          <div className="ms-Grid-col ms-sm4 form-item">
            <TextField
              value={anioNacimiento}
              // onChange={(ev, newValue) => setAnioNacimiento(newValue!)}
              onChange={(ev, newValue) => {
                if (!isNaN(+newValue!)) {
                  setAnioNacimiento(newValue!);
                }
              }}
              label="Año"
            />
          </div>
          {!fechaNacimientoIsValid && (
            <div className="ms-Grid-col ms-sm12 form-item mt-1">
              <Text className="ms-TextField-errorMessage" styles={{ root: { color: 'red' } }}>
                Debe ser mayor de 18 años
              </Text>
            </div>
          )}
        </div>

        <div className="ms-Grid-row mt-2">
          <div className="ms-Grid-col ms-sm12 form-item">
            {/* <InputField input={form.getGeneroInput()} label="Género" onBlur={onBlurCustomInput} /> */}
            {/* <ComboBoxInput
              input={form.getGeneroInput()}
              label={'Género (*)'}
              onBlur={onBlurCustomInput}
              options={GeneroOptions}
            /> */}
            <ChoiceGroup
              selectedKey={form.getGeneroInput().getValue()}
              options={GeneroOptions.map((option) => ({
                key: option.key,
                text: option.text,
              }))}
              onChange={(e: any, option: any) => {
                form.getGeneroInput().setValue(option?.key);
                onBlurCustomInput(form.getGeneroInput());
              }}
            />
          </div>
        </div>

        <div className="ms-Grid-row mt-2">
          <div className="ms-Grid-col ms-sm12 form-item">
            <FederationBoxNoBG
              system={system}
              courier={courier}
              input={federacionInput}
              onBlurCustomInput={onBlurCustomInput}
              // onChangeExtended={(f) => federacionObjInput.setValue(f)}
            />
          </div>
        </div>

        <div className="ms-Grid-row mt-2">
          <div className="ms-Grid-col ms-sm6 form-item">
            <Stack horizontalAlign="center" className="text-center mt-1">
              {' '}
              <SingleFileDropzone
                label="Documento"
                icon="Camera"
                onDrop={(file) => onDropDocumento(file)}
              ></SingleFileDropzone>
            </Stack>{' '}
            <div className="text-center">
              <Text variant="xSmall" className="" title="Formatos admitidos">
                (JPG, JPEG, GIF, PNG)
              </Text>
            </div>
            {fileDocumento && (
              <div className="mt-2 text-center">
                <Text block variant="medium" className="">
                  <Icon iconName="SkypeCircleCheck" style={{ color: '#107C10', marginRight: 10 }}></Icon>
                  Archivo {obtenerExtension(fileDocumento.name)} cargado -{' '}
                  {formatFileSize(fileDocumento.size)}
                </Text>
                {!checkMaxSize(fileDocumento.size) && (
                  <Text block variant="medium" className="error-color">
                    El tamaño de la foto es demasiado grande, el maximo es de 20MB
                  </Text>
                )}
              </div>
            )}
          </div>

          <div className="ms-Grid-col ms-sm6 form-item">
            <Stack horizontalAlign="center" className="text-center mt-1">
              {' '}
              <SingleFileDropzone
                label="Selfie"
                icon="Camera"
                onDrop={(file) => onDropSelfie(file)}
              ></SingleFileDropzone>
            </Stack>{' '}
            <div className="text-center">
              <Text variant="xSmall" className="" title="Formatos admitidos">
                (JPG, JPEG, GIF, PNG)
              </Text>
            </div>
            {fileSelfie && (
              <div className="mt-2 text-center">
                <Text block variant="medium" className="">
                  <Icon iconName="SkypeCircleCheck" style={{ color: '#107C10', marginRight: 10 }}></Icon>
                  Archivo {obtenerExtension(fileSelfie.name)} cargado - {formatFileSize(fileSelfie.size)}
                </Text>
                {!checkMaxSize(fileSelfie.size) && (
                  <Text block variant="medium" className="error-color">
                    El tamaño de la foto es demasiado grande, el maximo es de 20MB
                  </Text>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-2">
          <Text variant="small" className="error-color">
            <Icon
              iconName="AlertSolid"
              className="error-color"
              style={{ position: 'relative', top: 2, marginRight: 3 }}
            ></Icon>
            El contenido subido queda bajo exclusiva responsabilidad del presentante
          </Text>
        </div>
      </div>
    </div>
  );
};

export const formatMiles = (value: string | number): string => {
  const ret = value
    .toString()
    .replace(/\D/g, '') // Elimina cualquier carácter no numérico
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // Agrega los puntos como separadores de miles
  return ret;
};

export const OnRenderOption = ({ option }: { option: ISelectableOption<any> | undefined }): JSX.Element => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        // padding: "8px 12px",
        cursor: 'pointer', // Asegura que parece clicable
        backgroundColor: 'transparent', // Fondo por defecto
        transition: 'background-color 0.2s', // Suaviza el cambio de color en hover
        maxWidth: '-webkit-fill-available',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f3f2f1'; // Cambia el fondo al hacer hover
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent'; // Vuelve al fondo por defecto
      }}
    >
      {/* Imagen del club */}
      <Image
        src={
          (option!.data as Club).getEscudo()
            ? (option!.data as Club).getEscudo()
            : 'images/escudo_default.jpg'
        }
        width={25}
        height={25}
        alt={option!.text}
      />
      {/* Texto de la opción */}
      <span
        className="text-overflow"
        style={{ maxWidth: 200, marginLeft: 8, flexGrow: 1, verticalAlign: 'middle' }}
      >
        {option!.text}
      </span>
    </div>
  );
};
